import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import * as XLSX from 'xlsx';

const ImportModal = ({ isOpen, onClose, onImport }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Confirm
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      processFile(selectedFile);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files[0];
    if (droppedFile) {
      setFile(droppedFile);
      processFile(droppedFile);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const processFile = async (file) => {
    setIsProcessing(true);
    setValidationErrors([]);

    try {
      const data = await readFile(file);
      const { items, errors } = validateAndFormatData(data);
      
      setPreviewData(items);
      setValidationErrors(errors);
      setStep(2);
    } catch (error) {
      console.error('Error processing file:', error);
      setValidationErrors([{ row: 0, message: 'Failed to read file. Please ensure it is a valid Excel or CSV file.' }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(firstSheet);
          resolve(jsonData);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const validateAndFormatData = (data) => {
    const items = [];
    const errors = [];

    // Required fields mapping
    const fieldMapping = {
      'SKU': ['sku', 'SKU', 'Sku', 'product_sku', 'item_sku'],
      'Item Name': ['name', 'Item Name', 'item_name', 'product_name', 'Name'],
      'Category': ['category', 'Category', 'item_category'],
      'Supplier': ['supplier', 'Supplier', 'vendor', 'Vendor'],
      'Unit Price': ['unitPrice', 'Unit Price', 'unit_price', 'price', 'Price'],
      'Current Stock': ['currentStock', 'Current Stock', 'current_stock', 'stock', 'Stock', 'quantity', 'Quantity'],
      'Reorder Point': ['reorderPoint', 'Reorder Point', 'reorder_point', 'min_stock', 'minimum']
    };

    data.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because Excel starts at 1 and has header row
      const item = {
        id: `import-${Date.now()}-${index}`,
        description: '',
        status: 'in-stock'
      };

      // Extract and validate each field
      let hasErrors = false;

      // SKU (required)
      const sku = findFieldValue(row, fieldMapping['SKU']);
      if (!sku) {
        errors.push({ row: rowNumber, field: 'SKU', message: 'SKU is required' });
        hasErrors = true;
      } else {
        item.sku = String(sku).trim();
      }

      // Item Name (required)
      const name = findFieldValue(row, fieldMapping['Item Name']);
      if (!name) {
        errors.push({ row: rowNumber, field: 'Item Name', message: 'Item Name is required' });
        hasErrors = true;
      } else {
        item.name = String(name).trim();
      }

      // Category (required)
      const category = findFieldValue(row, fieldMapping['Category']);
      if (!category) {
        errors.push({ row: rowNumber, field: 'Category', message: 'Category is required' });
        hasErrors = true;
      } else {
        item.category = String(category).trim();
      }

      // Supplier (required)
      const supplier = findFieldValue(row, fieldMapping['Supplier']);
      if (!supplier) {
        errors.push({ row: rowNumber, field: 'Supplier', message: 'Supplier is required' });
        hasErrors = true;
      } else {
        item.supplier = String(supplier).trim();
      }

      // Unit Price (required, must be number)
      const unitPrice = findFieldValue(row, fieldMapping['Unit Price']);
      if (!unitPrice && unitPrice !== 0) {
        errors.push({ row: rowNumber, field: 'Unit Price', message: 'Unit Price is required' });
        hasErrors = true;
      } else {
        const price = parseFloat(unitPrice);
        if (isNaN(price) || price < 0) {
          errors.push({ row: rowNumber, field: 'Unit Price', message: 'Unit Price must be a valid positive number' });
          hasErrors = true;
        } else {
          item.unitPrice = price;
        }
      }

      // Current Stock (required, must be number)
      const currentStock = findFieldValue(row, fieldMapping['Current Stock']);
      if (!currentStock && currentStock !== 0) {
        errors.push({ row: rowNumber, field: 'Current Stock', message: 'Current Stock is required' });
        hasErrors = true;
      } else {
        const stock = parseInt(currentStock);
        if (isNaN(stock) || stock < 0) {
          errors.push({ row: rowNumber, field: 'Current Stock', message: 'Current Stock must be a valid positive number' });
          hasErrors = true;
        } else {
          item.currentStock = stock;
        }
      }

      // Reorder Point (required, must be number)
      const reorderPoint = findFieldValue(row, fieldMapping['Reorder Point']);
      if (!reorderPoint && reorderPoint !== 0) {
        errors.push({ row: rowNumber, field: 'Reorder Point', message: 'Reorder Point is required' });
        hasErrors = true;
      } else {
        const reorder = parseInt(reorderPoint);
        if (isNaN(reorder) || reorder < 0) {
          errors.push({ row: rowNumber, field: 'Reorder Point', message: 'Reorder Point must be a valid positive number' });
          hasErrors = true;
        } else {
          item.reorderPoint = reorder;
        }
      }

      // Optional: Description
      const description = findFieldValue(row, ['description', 'Description', 'desc', 'Desc']);
      if (description) {
        item.description = String(description).trim();
      }

      // Calculate status based on stock levels
      if (item.currentStock !== undefined && item.reorderPoint !== undefined) {
        if (item.currentStock === 0) {
          item.status = 'out-of-stock';
        } else if (item.currentStock <= item.reorderPoint) {
          item.status = 'low-stock';
        } else if (item.currentStock > item.reorderPoint * 3) {
          item.status = 'overstock';
        } else {
          item.status = 'in-stock';
        }
      }

      // Add timestamps
      item.createdAt = new Date().toISOString();
      item.updatedAt = new Date().toISOString();

      // Only add item if no critical errors
      if (!hasErrors) {
        items.push(item);
      }
    });

    return { items, errors };
  };

  const findFieldValue = (row, possibleKeys) => {
    for (const key of possibleKeys) {
      if (row.hasOwnProperty(key) && row[key] !== null && row[key] !== undefined && row[key] !== '') {
        return row[key];
      }
    }
    return null;
  };

  const handleImport = () => {
    if (previewData.length > 0) {
      onImport(previewData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setStep(1);
    setIsProcessing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const downloadTemplate = () => {
    const template = [
      {
        'SKU': 'SAMPLE-001',
        'Item Name': 'Sample Product',
        'Description': 'Sample description',
        'Category': 'Electronics',
        'Supplier': 'TechCorp Solutions',
        'Unit Price': 99.99,
        'Current Stock': 50,
        'Reorder Point': 10
      },
      {
        'SKU': 'SAMPLE-002',
        'Item Name': 'Another Product',
        'Description': 'Another description',
        'Category': 'Office Supplies',
        'Supplier': 'Office Pro Ltd',
        'Unit Price': 25.50,
        'Current Stock': 100,
        'Reorder Point': 20
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory Template');
    XLSX.writeFile(wb, 'Inventory_Import_Template.xlsx');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-500 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Import Inventory Items</h2>
              <p className="text-sm text-muted-foreground">
                {step === 1 ? 'Upload Excel or CSV file' : step === 2 ? 'Review and validate data' : 'Confirm import'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            iconName="X"
            disabled={isProcessing}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Download Template */}
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-accent mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground">First time importing?</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Download our template to see the required format and column headers.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadTemplate}
                      iconName="Download"
                      iconPosition="left"
                      className="mt-3"
                    >
                      Download Template
                    </Button>
                  </div>
                </div>
              </div>

              {/* File Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-primary/50 hover:bg-muted/50 transition-smooth cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Upload" size={32} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-lg font-medium text-foreground">
                      Drop your file here or click to browse
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Supports Excel (.xlsx, .xls) and CSV files
                    </p>
                  </div>
                  {file && (
                    <div className="flex items-center space-x-2 text-sm text-primary">
                      <Icon name="File" size={16} />
                      <span>{file.name}</span>
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* Required Fields Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3">Required Columns:</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>SKU</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Item Name</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Category</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Supplier</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Unit Price</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Current Stock</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Check" size={16} className="text-success" />
                    <span>Reorder Point</span>
                  </div>
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <Icon name="Minus" size={16} />
                    <span>Description (optional)</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Preview & Validate */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                    <div>
                      <p className="text-2xl font-bold text-foreground">{previewData.length}</p>
                      <p className="text-sm text-muted-foreground">Valid Items</p>
                    </div>
                  </div>
                </div>
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="AlertCircle" size={24} className="text-error" />
                    <div>
                      <p className="text-2xl font-bold text-foreground">{validationErrors.length}</p>
                      <p className="text-sm text-muted-foreground">Errors Found</p>
                    </div>
                  </div>
                </div>
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="File" size={24} className="text-accent" />
                    <div>
                      <p className="text-2xl font-bold text-foreground">{file?.name?.slice(0, 15)}...</p>
                      <p className="text-sm text-muted-foreground">Source File</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="AlertTriangle" size={20} className="text-error mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-medium text-foreground">Validation Errors</h4>
                      <p className="text-sm text-muted-foreground mb-3">
                        The following rows have errors and will not be imported:
                      </p>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {validationErrors.map((error, index) => (
                          <div key={index} className="text-sm text-error font-mono">
                            Row {error.row}: {error.field} - {error.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Table */}
              {previewData.length > 0 && (
                <div>
                  <h4 className="font-medium text-foreground mb-3">Preview ({previewData.length} items)</h4>
                  <div className="border border-border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-96">
                      <table className="w-full">
                        <thead className="bg-muted/50 border-b border-border sticky top-0">
                          <tr>
                            <th className="text-left p-3 text-sm font-medium text-foreground">SKU</th>
                            <th className="text-left p-3 text-sm font-medium text-foreground">Item Name</th>
                            <th className="text-left p-3 text-sm font-medium text-foreground">Category</th>
                            <th className="text-left p-3 text-sm font-medium text-foreground">Supplier</th>
                            <th className="text-left p-3 text-sm font-medium text-foreground">Price</th>
                            <th className="text-left p-3 text-sm font-medium text-foreground">Stock</th>
                            <th className="text-left p-3 text-sm font-medium text-foreground">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.map((item, index) => (
                            <tr key={index} className="border-b border-border hover:bg-muted/30">
                              <td className="p-3 text-sm font-mono">{item.sku}</td>
                              <td className="p-3 text-sm">{item.name}</td>
                              <td className="p-3 text-sm">{item.category}</td>
                              <td className="p-3 text-sm">{item.supplier}</td>
                              <td className="p-3 text-sm">${item.unitPrice.toFixed(2)}</td>
                              <td className="p-3 text-sm">{item.currentStock}</td>
                              <td className="p-3 text-sm">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                                  item.status === 'in-stock' ? 'bg-success/10 text-success border-success/20' :
                                  item.status === 'low-stock' ? 'bg-warning/10 text-warning border-warning/20' :
                                  item.status === 'out-of-stock' ? 'bg-error/10 text-error border-error/20' :
                                  'bg-accent/10 text-accent border-accent/20'
                                }`}>
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div>
            {step === 2 && previewData.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Ready to import {previewData.length} item{previewData.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {step === 2 && (
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                disabled={isProcessing}
              >
                Back
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            {step === 2 && previewData.length > 0 && (
              <Button
                onClick={handleImport}
                iconName="Check"
                iconPosition="left"
                disabled={isProcessing}
              >
                Import Items
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;
